<template>
    <filter-modal-shell name="advanced-filters-modal" :title="$t('frontend_groovesell_modal_advanced_filters')"
        :active-count="activeCount" :can-apply="!defaultFilters" :apply-label="$t('frontend_analytics_apply_filters')"
        @apply="emitStartSearch" @reset="resetFilters">

        <section class="afm__section">
            <div class="afm__section-head">
                <h3 class="afm__section-title">{{ $t('frontend_analytics_funnels') }}</h3>
                <p class="afm__section-hint">Tick a funnel for everything in it, or drill into products and price points.</p>
            </div>
            <funnel-picker :options="funnelsDropdown" :value="filteredFunnels" @input="filteredFunnelsChanged" />
        </section>

        <section class="afm__section">
            <div class="afm__section-head">
                <h3 class="afm__section-title">{{ $t('frontend_analytics_affiliates') }}</h3>
            </div>
            <affiliate-scope :value="filteredAffiliates" :label="$t('frontend_analytics_affiliates')"
                @input="affiliatesChanged('filteredAffiliates', $event)" />
        </section>

        <section class="afm__section">
            <div class="afm__section-head">
                <h3 class="afm__section-title">{{ $t('frontend_analytics_second_tier_affiliates') }}</h3>
            </div>
            <affiliate-scope :value="filteredSecondTierAffiliates" :label="$t('frontend_analytics_second_tier_affiliates')"
                @input="affiliatesChanged('filteredSecondTierAffiliates', $event)" />
        </section>

        <section class="afm__section">
            <div class="afm__section-head">
                <h3 class="afm__section-title">{{ $t('frontend_analytics_advance_transaction') }}</h3>
            </div>
            <div class="afm__pills" role="radiogroup" :aria-label="$t('frontend_analytics_advance_transaction')">
                <button v-for="opt in filteredTransactionOptions" :key="opt.value" type="button" role="radio"
                    class="afm__pill" :class="{ 'is-active': filteredAdvancedTransactions == opt.value }"
                    :aria-checked="filteredAdvancedTransactions == opt.value" @click="setAdvancedTransactions(opt.value)">
                    {{ opt.text }}
                </button>
            </div>
        </section>
    </filter-modal-shell>
</template>
<script>
import FilterModalShell from '../parts/FilterModalShell';
import FunnelPicker from '../parts/FunnelPicker';
import AffiliateScope from '../parts/AffiliateScope';

export default {
    components: {
        FilterModalShell,
        FunnelPicker,
        AffiliateScope,
    },
    props: {
        filteredFunnels: {
            type: Array,
            default: () => [],
        },
        funnelsDropdown: {
            type: Array,
            default: () => [],
        },
        filteredAffiliates: {
            type: Array,
            default: () => [],
        },
        filteredSecondTierAffiliates: {
            type: Array,
            default: () => [],
        },
        filteredAdvancedTransactions: {
            default: 0,
        },
    },
    computed: {
        defaultFilters() {
            return this.filteredAdvancedTransactions == 0 &&
            this.filteredFunnels?.length == 0 &&
            this.isAll(this.filteredAffiliates) &&
            this.isAll(this.filteredSecondTierAffiliates);
        },
        activeCount() {
            let n = 0;
            if (this.filteredFunnels?.length > 0) n += 1;
            if (!this.isAll(this.filteredAffiliates)) n += 1;
            if (!this.isAll(this.filteredSecondTierAffiliates)) n += 1;
            if (this.filteredAdvancedTransactions != 0) n += 1;
            return n;
        },
        filteredTransactionOptions() {
            return [
                { value: 0, text: this.$t('frontend_analytics_all') },
                { value: 1, text: this.$t('frontend_analytics_first_sale_or') },
                { value: 2, text: this.$t('frontend_analytics_first_sale_after_trail') },
                { value: 3, text: this.$t('frontend_analytics_rebills_only') }
            ];
        },
    },

    methods: {
        isAll(list) {
            return list?.length == 1 && list[0].id == 0 && list[0].name === 'All';
        },
        setAdvancedTransactions(value) {
            this.$emit('filter-changed', {
                key: 'filteredAdvancedTransactions',
                value,
            });
        },
        filteredFunnelsChanged(value) {
            this.$emit('filter-changed', {
                key: 'filteredFunnels',
                value,
            })
        },
        affiliatesChanged(key, value) {
            this.$emit('filter-changed', {
                key,
                value,
                startSearch: false
            })
        },
        resetFilters() {
            this.$emit('filter-changed', {
                key: 'clear',
                values: [
                    { key: 'filteredFunnels', value: [] },
                    { key: 'filteredAffiliates', value: [{ id: 0, name: 'All' }] },
                    { key: 'filteredSecondTierAffiliates', value: [{ id: 0, name: 'All' }] },
                    { key: 'filteredAdvancedTransactions', value: 0 },
                ],
            });
        },
        emitStartSearch() {
            if (this.filteredAffiliates.length === 0) {
                this.$emit('filter-changed', {
                    key: 'filteredAffiliates',
                    value: [{ id: -2, name: 'Only Affiliates' }],
                });
            }
            if (this.filteredSecondTierAffiliates.length === 0) {
                this.$emit('filter-changed', {
                    key: 'filteredSecondTierAffiliates',
                    value: [{ id: -2, name: 'Only Affiliates' }],
                });
            }

            this.$emit('apply-filters');
            this.$root.$emit('modal-close', 'advanced-filters-modal');
        },
    }
}
</script>
<style lang="scss">
.afm__pills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.afm__pill {
    padding: 8px 14px;
    border-radius: 999px;
    border: 1px solid var(--af-border);
    background: #fff;
    font-size: 13.5px;
    font-weight: 600;
    color: var(--af-muted);
    cursor: pointer;
    transition: border-color 120ms ease, background 120ms ease, color 120ms ease;

    &:hover { border-color: var(--af-border-strong); color: var(--af-fg); }
    &:focus-visible { outline: none; box-shadow: 0 0 0 3px var(--af-primary-ring); }

    &.is-active {
        border-color: var(--af-primary);
        background: var(--af-primary-soft);
        color: var(--af-primary);
    }
}
</style>
